"use client";

import { Clock, Mail, MessageCircleQuestionMark as MessageCircleQuestion, Phone } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ConnectForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border rounded-lg" style={{
      backgroundColor: "var(--card)",
      borderColor: "var(--border)"
    }}>
        <p className="font-default text-2xl text-foreground mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-sm border border-border bg-background text-foreground hover:bg-accent">
          Send another message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Your name is required",
        minLength: {
          value: 2,
          message: "At least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono uppercase text-xs tracking-widest text-muted-foreground">
                School Name
              </FormLabel>
              <FormControl>
                <Input placeholder="e.g. St. Jude's Academy" className="h-14 border-b-2 border-border bg-transparent text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="role" rules={{
        required: "Your role is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono uppercase text-xs tracking-widest text-muted-foreground">
                Your Role
              </FormLabel>
              <FormControl>
                <Input placeholder="e.g. Head of Department" className="h-14 border-b-2 border-border bg-transparent text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="students" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono uppercase text-xs tracking-widest text-muted-foreground">
              Number of Students
            </FormLabel>
            <FormControl>
              <Input type="number" placeholder="e.g. 500" className="h-14 border-b-2 border-border bg-transparent text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-0" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono uppercase text-xs tracking-widest text-muted-foreground">
              Subject Area of Interest
            </FormLabel>
            <FormControl>
              <Textarea rows={3} placeholder="Tell us which subjects you'd like to pilot with..." className="min-h-32 border-b-2 border-border bg-transparent text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-0 p-4" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="consent" render={({
      field
    }) => <FormItem className="flex flex-row items-start gap-3 space-y-0">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-1 size-5 rounded-sm border border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
            </FormControl>
            <FormLabel className="text-sm text-muted-foreground leading-relaxed font-normal cursor-pointer">
              I agree to receive updates regarding the Setura pilot program.
            </FormLabel>
          </FormItem>} />

      {submitError && <div className="p-4 rounded-lg border border-destructive" style={{
      backgroundColor: "hsl(0 84% 60% / 0.1)"
    }}>
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto md:ml-auto md:flex h-14 px-8 rounded-sm bg-primary text-primary-foreground font-semibold text-base hover:bg-primary/90 transition-all">
        {isSubmitting ? "Sending..." : "Book a pilot call"}
      </Button>
    </Form>;
}
export default function Conversation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const contactInfo = [{
    icon: Mail,
    label: "Email",
    value: "partnerships@resseti.com"
  }, {
    icon: Phone,
    label: "Phone",
    value: "+44 (0) 1223 317070"
  }, {
    icon: Clock,
    label: "Hours",
    value: "Mon - Fri, 9am - 5pm"
  }];
  return <section data-section-id="1317" id="start-a-conversation" className="bg-background text-foreground py-24 md:py-36 px-4 md:px-8 lg:px-12 overflow-hidden">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono uppercase text-xs tracking-widest text-muted-foreground">
                START A CONVERSATION
              </span>
              <span className="inline-block size-2 rounded-sm" style={{
              backgroundColor: "var(--primary)"
            }} />
            </div>

            <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-medium leading-tight tracking-tight text-foreground mb-6">
              Explore the <span className="italic">Setura</span> diagnostic engine.
            </h1>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-[56ch] mb-10">
              We work with forward-thinking schools to map student performance against mark-scheme logic. Reach out to discuss how Setura can surface specific process failures in your classrooms.
            </p>

            <FormProvider formKey="conversation_form_pilot" formId="conversation_form_pilot" sectionName="start-a-conversation" defaultValues={{
            name: "",
            role: "",
            students: "",
            message: "",
            consent: false
          }} validationRules={{
            name: {
              required: true
            },
            role: {
              required: true
            }
          }} submitText="Book a pilot call" successMessage="Thank you. A member of our team will reach out to discuss your pilot requirements." errorMessage="Something went wrong. Please try again.">
              <ConnectForm />
            </FormProvider>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2 lg:sticky lg:top-24">
            <div className="relative mb-10">
              <div className="relative mx-auto rounded-lg border border-border p-8 md:p-10 flex items-center justify-center" style={{
              backgroundColor: "var(--card)",
              maxWidth: "420px",
              minHeight: "320px"
            }}>
                <svg viewBox="0 0 240 280" className="w-full max-w-[240px] h-auto" aria-label="Resseti diagnostic schematic">
                  <line x1="120" y1="60" x2="120" y2="30" stroke="var(--foreground)" strokeWidth="2" />
                  <circle cx="120" cy="24" r="8" fill="var(--primary)" />
                  <rect x="55" y="55" width="130" height="110" rx="2" fill="transparent" stroke="var(--foreground)" strokeWidth="2" />
                  <circle cx="95" cy="105" r="6" fill="var(--foreground)" />
                  <circle cx="145" cy="105" r="6" fill="var(--foreground)" />
                  <path d="M 100 135 Q 120 150 140 135" fill="none" stroke="var(--foreground)" strokeWidth="2" />
                </svg>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-4">
              {contactInfo.map((info, i) => {
              const Icon = info.icon;
              return <div key={i} data-index={i} className="rounded-lg border border-border p-5 flex items-center gap-4" style={{
                backgroundColor: "var(--card)"
              }}>
                    <div className="shrink-0 size-10 rounded-sm border border-border flex items-center justify-center">
                      <Icon className="size-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-mono uppercase text-[10px] tracking-widest text-muted-foreground mb-1">
                        {info.label}
                      </div>
                      <div className="font-default text-sm text-foreground truncate">
                        {info.value}
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}